import { useEffect, useMemo } from "react"
import useSWR from "swr"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { ADSB_POLL_MS, ADSB_DEAD_RECKONING_INTERVAL } from "@nova/constants"
import { useTick } from "@nova/toolkit-pure-hooks"
import { useAppSelector } from "integrations/redux/hooks"
import {
    selectAdsbCircleCenter,
    selectAdsbEnabled,
    selectAdsbIsPlacingCircle,
    selectAircraftTypes,
} from "features/overview/slice"
import { projectAircraftPosition } from "./deadReckoning"
import { setAdsbLivePositions } from "./aircraftLivePositionStore"

export const useAdsbTracking = (orgId: string | undefined) => {
    const aircraftTypes = useAppSelector(selectAircraftTypes)
    const enabled = useAppSelector(selectAdsbEnabled)
    const isPlacingCircle = useAppSelector(selectAdsbIsPlacingCircle)
    const circleCenter = useAppSelector(selectAdsbCircleCenter)

    const swr = useSWR(
        orgId && enabled && circleCenter && !isPlacingCircle
            ? ["adsb", orgId, circleCenter.lat, circleCenter.lng]
            : null,
        () =>
            fetchApiGateway("get /org/:orgId/adsb", {
                params: { orgId: orgId! },
                query: `centerLat=${circleCenter!.lat}&centerLng=${circleCenter!.lng}`,
            }),
        { refreshInterval: ADSB_POLL_MS, revalidateOnFocus: false, shouldRetryOnError: false }
    )

    useTick(ADSB_DEAD_RECKONING_INTERVAL)
    const now = Date.now()

    const data = useMemo(() => {
        if (!swr.data) return swr.data
        return swr.data.aircraft
            .filter((a) => aircraftTypes[a.type])
            .map((a) => projectAircraftPosition(a, now))
    }, [swr.data, aircraftTypes, now])

    useEffect(() => setAdsbLivePositions(data ?? []), [data])

    return { ...swr, data, rawAircraft: swr.data?.aircraft }
}
